import React, { useEffect, useRef } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { colors } from '../../theme/colors.js';

/* ═══════════════════════════════════════════════════════════════
   DESIGN SYSTEM — Karobar Landing Page (Riley-inspired)
   ═══════════════════════════════════════════════════════════════ */

/* ─── Palette ─── */
export const P = colors.primary;       // #4527A0
export const PL = colors.primaryLight; // #7E57C2
export const PD = colors.primaryDark;  // #311B92
export const A = colors.accent;        // #FF2D55
export const AL = colors.accentLight;  // #FF6482

/* ─── Color-block backgrounds (Riley pattern) ─── */
export const BLOCKS = {
  white: '#FFFFFF',
  cream: '#FDF8F3',
  lavender: '#F5F0FF',
  deepPurple: PD,
  lightPink: '#FFF5F7',
  slate: '#F7F7FB',
};

/* ─── Scroll-reveal section wrapper ─── */
export const Section = ({ children, bg, dark, sx }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          el.style.opacity = 1;
          el.style.transform = 'translateY(0)';
        }
      },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        width: '100%',
        opacity: 0,
        transform: 'translateY(40px)',
        transition: 'opacity 0.9s cubic-bezier(.16,1,.3,1), transform 0.9s cubic-bezier(.16,1,.3,1)',
        background: bg || 'transparent',
        color: dark ? '#fff' : 'inherit',
        ...sx,
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2.5, sm: 4, md: 7, lg: 10 }, py: { xs: 8, md: 14 } }}>
        {children}
      </Box>
    </Box>
  );
};

/* ─── Color Block — full-width colored container with optional rounded inner box ─── */
export const ColorBlock = ({ children, bg = BLOCKS.lavender, dark, innerBg, innerRadius = '28px', sx, innerSx }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          el.style.opacity = 1;
          el.style.transform = 'translateY(0)';
        }
      },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        width: '100%',
        opacity: 0,
        transform: 'translateY(40px)',
        transition: 'opacity 0.9s cubic-bezier(.16,1,.3,1), transform 0.9s cubic-bezier(.16,1,.3,1)',
        background: bg,
        color: dark ? '#fff' : 'inherit',
        py: { xs: 6, md: 10 },
        px: { xs: 2, sm: 3, md: 5 },
        ...sx,
      }}
    >
      {innerBg ? (
        <Box
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            background: innerBg,
            borderRadius: innerRadius,
            px: { xs: 3, sm: 4, md: 7 },
            py: { xs: 5, md: 8 },
            ...innerSx,
          }}
        >
          {children}
        </Box>
      ) : (
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 1, sm: 2, md: 3 }, ...innerSx }}>
          {children}
        </Box>
      )}
    </Box>
  );
};

/* ─── Glass card ─── */
export const Glass = ({ children, sx, ...rest }) => (
  <Box
    {...rest}
    sx={{
      background: 'rgba(255,255,255,0.55)',
      backdropFilter: 'blur(24px)',
      WebkitBackdropFilter: 'blur(24px)',
      borderRadius: '20px',
      border: '1px solid rgba(255,255,255,0.45)',
      boxShadow: '0 8px 32px rgba(69,39,160,0.06), 0 1.5px 6px rgba(0,0,0,0.03)',
      p: { xs: 3, md: 4 },
      position: 'relative',
      overflow: 'hidden',
      ...sx,
    }}
  />
);

/* ─── Section image ─── */
export const SectionImage = ({ src, alt, sx }) => (
  <Box
    component="img"
    src={src}
    alt={alt}
    loading="lazy"
    sx={{
      width: '100%',
      height: 'auto',
      borderRadius: '16px',
      boxShadow: '0 24px 64px rgba(69,39,160,0.10), 0 4px 16px rgba(0,0,0,0.05)',
      transition: 'transform 0.6s cubic-bezier(.16,1,.3,1)',
      '&:hover': { transform: 'translateY(-4px)' },
      ...sx,
    }}
  />
);

/* ─── Section heading ─── */
export const SectionHeading = ({ children, variant = 'plain', size = 'md', align = 'left', sx }) => {
  const sizes = {
    sm: { xs: '1.1rem', md: '1.35rem' },
    md: { xs: '1.5rem', sm: '1.8rem', md: '2.2rem' },
    lg: { xs: '2rem', sm: '2.6rem', md: '3.2rem', lg: '3.6rem' },
  };
  return (
    <Typography
      component="h2"
      sx={{
        fontSize: sizes[size] || sizes.md,
        fontWeight: 300,
        fontFamily: "'Inter', sans-serif",
        lineHeight: 1.12,
        letterSpacing: '-0.03em',
        // Mobile: always left-aligned; desktop honours the `align` prop
        textAlign: { xs: 'left', md: align },
        ...(variant === 'gradient' ? {
          background: `linear-gradient(135deg, ${PD} 0%, ${P} 40%, ${A} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        } : { color: 'inherit' }),
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
};

/* ─── Body text ─── */
export const BodyText = ({ children, muted, align = 'left', sx }) => (
  <Typography
    sx={{
      fontSize: { xs: '0.95rem', md: '1.05rem' },
      fontWeight: 400,
      color: muted ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.75)',
      lineHeight: 1.75,
      // Mobile: always left-aligned; desktop honours the `align` prop
      textAlign: { xs: 'left', md: align },
      ...sx,
    }}
  >
    {children}
  </Typography>
);

/* ─── Feature pill chip ─── */
export const FeaturePill = ({ icon, label, color = P }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      px: 2,
      py: 0.8,
      borderRadius: '10px',
      background: `${color}08`,
      border: `1px solid ${color}18`,
    }}
  >
    {icon && <Box sx={{ display: 'flex' }}>{icon}</Box>}
    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color, whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
  </Box>
);

/* ─── Sub line ─── */
export const SubLine = ({ children, align = 'left', sx }) => (
  <Typography
    sx={{
      fontSize: { xs: '1rem', md: '1.15rem' },
      fontWeight: 400,
      color: 'rgba(0,0,0,0.55)',
      lineHeight: 1.7,
      // Mobile: always left-aligned; desktop honours the `align` prop
      textAlign: { xs: 'left', md: align },
      ...sx,
    }}
  >
    {children}
  </Typography>
);

/* ─── Gradient accent line ─── */
export const AccentLine = ({ sx }) => (
  <Box
    sx={{
      width: 48,
      height: 3,
      borderRadius: 2,
      background: `linear-gradient(90deg, ${P}, ${A})`,
      mb: 3,
      ...sx,
    }}
  />
);

/* ─── Mobile card wrapper — only wraps on mobile ─── */
export const MobileCard = ({ children, sx }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  if (!isMobile) return children;
  return (
    <Box
      sx={{
        borderRadius: '24px',
        background: 'rgba(255,255,255,0.75)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(255,255,255,0.6)',
        boxShadow: '0 4px 24px rgba(69,39,160,0.05)',
        p: 3,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

/* ─── Mid-page CTA banner (Riley-style) ─── */
export const CTABanner = ({ onClick, headline, sub, buttonText = 'Get Started Free' }) => (
  <Box
    sx={{
      width: '100%',
      py: { xs: 6, md: 8 },
      px: { xs: 3, md: 6 },
      background: `linear-gradient(135deg, ${PD} 0%, ${P} 50%, ${A}CC 100%)`,
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '-50%',
        right: '-20%',
        width: '60%',
        height: '200%',
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.04)',
      },
    }}
  >
    <Typography
      sx={{
        fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.2rem' },
        fontWeight: 800,
        color: '#fff',
        letterSpacing: '-0.02em',
        lineHeight: 1.2,
        mb: 1.5,
        position: 'relative',
      }}
    >
      {headline}
    </Typography>
    {sub && (
      <Typography sx={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.7)', mb: 4, position: 'relative' }}>
        {sub}
      </Typography>
    )}
    <Box
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 4,
        py: 1.8,
        borderRadius: '14px',
        background: '#fff',
        color: PD,
        fontWeight: 700,
        fontSize: '0.95rem',
        cursor: 'pointer',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        transition: 'all 0.3s cubic-bezier(.16,1,.3,1)',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        },
      }}
    >
      {buttonText}
    </Box>
  </Box>
);
